/**
 * The guided-demo request page.
 *
 * "Every 'Request a demo' link opens the same audience-specific request form"
 * (§4), so it is one route rather than a modal repeated on four screens. It
 * carries the session token when there is one, which is how the request
 * arrives with the visitor's brief and audience attached.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../app/http-common';
import { isAudience } from './audienceCopy';
import CampaignShell from './CampaignShell';
import MockShell from './mockup/MockShell';
import { MockDemo } from './mockup/MockProse';
import { isMockup, resolveVariant } from './campaignVariant';
import { EVENTS, track } from '../../analytics/campaignAnalytics';
import DemoRequestForm from './DemoRequestForm';
import './Campaign.css';

const tokenKeyFor = (audience) => `alchemiq.campaign.session.${audience}`;

export default function CampaignDemoPage() {
  const { audience } = useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = useCallback(async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      let sessionToken = null;
      try { sessionToken = window.localStorage.getItem(tokenKeyFor(audience)); } catch { /* private mode */ }
      await api.submitCampaignDemoRequest({
        ...values, audience, sessionToken, originScreen: 'demo_page',
      });
      track(EVENTS.DEMO_REQUEST, { audience, origin_screen: 'demo_page' });
    } catch (err) {
      // §4 — "a failed submission keeps the entered information and offers a
      // retry". Rethrowing would clear the form's sent flag and leave them
      // with nothing.
      setError(err.response?.data?.error || 'We could not send that. Please try again.');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [audience]);

  if (!isAudience(audience)) return null;
  // Sticky for the journey — see campaignVariant.
  const mock = isMockup(resolveVariant());

  return mock ? (
    // 570px and centred — the mockup's demo column, measured off it.
    <MockShell audience={audience} rail={570} centered>
      <MockDemo
        onSubmit={submit}
        onBack={() => navigate(`/c/${audience}`)}
        submitting={submitting}
        error={error}
      />
    </MockShell>
  ) : (

    <CampaignShell audience={audience} variant="prose" hideCta>
      <DemoRequestForm
        onSubmit={submit}
        onBack={() => navigate(`/c/${audience}`)}
        submitting={submitting}
        error={error}
      />
    </CampaignShell>
  );
}
